import { submitFalQueueRequestOnce } from './config.js';
import { isCancellationError } from './utils.js';

function createAbortError(signal) {
  if (signal?.reason instanceof Error) {
    return signal.reason;
  }
  if (typeof globalThis.DOMException === 'function') {
    return new globalThis.DOMException('Operation cancelled', 'AbortError');
  }
  const error = new Error('Operation cancelled');
  error.name = 'AbortError';
  return error;
}

function createQueueCancellation(fal, modelId, signal, transportController) {
  let requestId = null;
  let cancellationRequested = false;
  const cancelQueuedRequest = () => {
    if (cancellationRequested || !requestId || typeof fal.queue?.cancel !== 'function') return;
    cancellationRequested = true;
    try {
      Promise.resolve(fal.queue.cancel(modelId, { requestId })).catch(() => {});
    } catch {
      // Provider cancellation is best-effort; the local operation still stops.
    }
  };
  const handleAbort = () => {
    if (!requestId) return;
    cancelQueuedRequest();
    transportController.abort(signal.reason);
  };
  signal?.addEventListener('abort', handleAbort, { once: true });
  return {
    setRequestId(value) {
      requestId = value;
      if (signal?.aborted) handleAbort();
    },
    release() { signal?.removeEventListener('abort', handleAbort); },
    cancelProviderJob: cancelQueuedRequest,
  };
}

function isDefiniteSubmitRejection(error) {
  const status = Number(error?.status ?? error?.statusCode);
  return Number.isInteger(status) && status >= 400 && status < 500
    && status !== 408 && status !== 429;
}

function isDefiniteResultRejection(error) {
  const status = Number(error?.status ?? error?.statusCode);
  return Number.isInteger(status) && status >= 400 && status < 500
    && ![401, 403, 404, 408, 429].includes(status);
}

function isMissingRequest(error) {
  return Number(error?.status ?? error?.statusCode) === 404;
}

function markPaidQueueFailure(error, { requestId = null, submitted = false } = {}) {
  const cause = error instanceof Error ? error : new Error(String(error));
  const definiteRejection = !requestId
    && (isDefiniteSubmitRejection(cause) || cause.submissionNotForwarded === true);
  const terminalProviderFailure = cause.terminalProviderFailure === true || definiteRejection;
  const recoverableRequest = Boolean(requestId && !terminalProviderFailure);
  const submissionMayHaveBeenAccepted = submitted && !requestId && !definiteRejection;
  const prefix = recoverableRequest
    ? `Paid request ${requestId} was accepted but its result is unresolved. Do not resubmit; recover this request ID.`
    : requestId
      ? `Paid request ${requestId} reached a terminal provider failure. Do not resubmit automatically.`
      : submissionMayHaveBeenAccepted
        ? 'Paid submission acceptance is unknown. Do not resubmit automatically.'
        : null;
  const failure = prefix ? new Error(`${prefix} ${cause.message}`, { cause }) : cause;
  if (prefix) failure.name = cause.name;
  failure.providerRequestId = requestId;
  failure.terminalProviderFailure = terminalProviderFailure;
  failure.recoverableRequest = recoverableRequest;
  failure.submissionMayHaveBeenAccepted = submissionMayHaveBeenAccepted;
  failure.paidSubmissionNonRetryable = Boolean(requestId || submissionMayHaveBeenAccepted);
  return failure;
}

/** Preserve one accepted-job failure contract through service-specific error copy. */
export function wrapPaidQueueFailure(error, message) {
  const failure = new Error(message, { cause: error });
  for (const key of ['providerRequestId', 'recoverableRequest', 'terminalProviderFailure',
    'submissionMayHaveBeenAccepted', 'paidSubmissionNonRetryable']) {
    if (error?.[key] !== undefined) failure[key] = error[key];
  }
  return failure;
}

/** Validate the common FAL image-array result inside the helper, so a bad answer settles its job. */
export function validateFalImageResult(response) {
  const images = response?.data?.images || response?.images;
  if (!Array.isArray(images) || images.length === 0 || images.some(image => {
    const url = typeof image === 'string' ? image : image?.url;
    return typeof url !== 'string' || !url.trim();
  })) {
    throw new Error('No images received from API');
  }
}

/** Object-only image endpoints cannot consume a bare URL string result. */
export function validateFalObjectImageResult(response) {
  validateFalImageResult(response);
  const images = response?.data?.images || response?.images;
  if (images.some(image => typeof image !== 'object' || !image?.url)) {
    throw new Error('No usable image objects received from API');
  }
}

function awaitQueueRead(readPromise, signal) {
  if (signal.aborted) return Promise.reject(createAbortError(signal));
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(createAbortError(signal));
    signal.addEventListener('abort', onAbort, { once: true });
    Promise.resolve(readPromise).then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', onAbort);
    });
  });
}

function waitForQueuePoll(delayMs, signal) {
  if (signal?.aborted) return Promise.reject(createAbortError(signal));
  return new Promise((resolve, reject) => {
    const timer = globalThis.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      globalThis.clearTimeout(timer);
      reject(createAbortError(signal));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** One paid POST, then only bounded reads of its acknowledged provider job. */
export async function subscribeFalQueueOnceWithCancellation(
  fal, modelId, options = {}, signal,
  { submitOnce = submitFalQueueRequestOnce, pollIntervalMs = 2_000,
    timeoutMs = 10 * 60_000, maxReadFailures = 5 } = {},
) {
  if (signal?.aborted) throw createAbortError(signal);
  const transportController = new AbortController();
  const cancellation = createQueueCancellation(fal, modelId, signal, transportController);
  let requestId = null;
  let submitted = false;
  let deadlineTimer = null;
  let submitTimer = null;
  let retainCancellationForLateReceipt = false;
  let callbackCancellation = null;
  const operationDeadline = Date.now() + timeoutMs;
  const resumeRequestId = typeof options.resumeRequestId === 'string'
    ? options.resumeRequestId.trim() : '';
  try {
    if (resumeRequestId) {
      requestId = resumeRequestId;
      cancellation.setRequestId(requestId);
    } else {
      // Caller abort during submit records intent. Aborting this POST could hide
      // an accepted job before its request ID arrives, so only reads use the signal.
      const submitController = new AbortController();
      const submitPromise = Promise.resolve().then(() => {
        if (signal?.aborted) throw createAbortError(signal);
        return submitOnce(modelId, options.input, {
          signal: submitController.signal,
          onDispatch: () => { submitted = true; },
        });
      });
      const submitDeadline = new Promise((_, reject) => {
        submitTimer = globalThis.setTimeout(() => {
          const timeout = new Error('FAL submission did not return before the deadline.');
          timeout.name = 'TimeoutError';
          if (!submitted) submitController.abort(timeout);
          reject(timeout);
        }, timeoutMs);
      });
      let receipt;
      try {
        receipt = await Promise.race([submitPromise, submitDeadline]);
      } catch (error) {
        if (submitted && error?.name === 'TimeoutError') {
          // The POST may have been accepted. Keep its in-flight response solely
          // for late cancellation; never issue another submit or resume this job.
          retainCancellationForLateReceipt = true;
          void submitPromise.then(async lateReceipt => {
            const lateId = typeof lateReceipt?.request_id === 'string'
              ? lateReceipt.request_id.trim() : '';
            if (lateId) {
              cancellation.setRequestId(lateId);
              if (options.onProviderResult) await options.onEnqueue?.(lateId);
            }
          }).catch(() => {}).finally(() => cancellation.release());
        }
        throw error;
      } finally {
        globalThis.clearTimeout(submitTimer);
      }
      requestId = typeof receipt?.request_id === 'string' && receipt.request_id.trim()
        ? receipt.request_id.trim() : null;
      if (!requestId) throw new Error('FAL submission returned no request ID.');
      cancellation.setRequestId(requestId);
    }

    const deadline = operationDeadline;
    deadlineTimer = globalThis.setTimeout(() => {
      const timeout = new Error('FAL read deadline exceeded.');
      timeout.name = 'TimeoutError';
      transportController.abort(timeout);
    }, Math.max(0, deadline - Date.now()));
    if (!resumeRequestId) {
      await awaitQueueRead(Promise.resolve().then(() => options.onEnqueue?.(requestId)), transportController.signal);
      if (signal?.aborted) throw createAbortError(signal);
    }
    let readFailures = 0;
    let lastReadError = null;
    while (Date.now() < deadline) {
      if (signal?.aborted) throw createAbortError(signal);
      let status;
      try {
        status = await awaitQueueRead(fal.queue.status(modelId, {
          requestId, logs: options.logs === true, abortSignal: transportController.signal,
        }), transportController.signal);
        if (status?.status !== 'COMPLETED') readFailures = 0;
      } catch (error) {
        if (signal?.aborted) throw createAbortError(signal);
        if (transportController.signal.aborted) throw createAbortError(transportController.signal);
        if (resumeRequestId && isMissingRequest(error)) {
          error.terminalProviderFailure = true;
          throw error;
        }
        lastReadError = error;
        readFailures += 1;
        if (readFailures >= maxReadFailures) throw error;
        await waitForQueuePoll(pollIntervalMs, transportController.signal);
        continue;
      }
      try {
        options.onQueueUpdate?.(status);
      } catch (error) {
        // Callers that cancel through a callback (checkCancelled) throw here instead of aborting
        // a signal. That is still the user cancelling this job, so the provider job stops too.
        if (isCancellationError(error)) {
          cancellation.cancelProviderJob();
          callbackCancellation = error;
        }
        throw error;
      }
      if (status?.status === 'COMPLETED') {
        let resultReadSucceeded = false;
        try {
          const resultPromise = Promise.resolve(fal.queue.result(modelId, {
            requestId, abortSignal: transportController.signal,
          })).then(async (value) => {
            if (options.onProviderResult) {
              resultReadSucceeded = true;
              options.validateResult?.(value);
              try { await options.onProviderResult(value); }
              catch (error) {
                const failure = new Error('Completed audio could not be retained; recover this paid request instead of resubmitting.', { cause: error });
                failure.completedResultRetentionFailed = true;
                throw failure;
              }
            }
            return value;
          });
          const result = await awaitQueueRead(resultPromise, transportController.signal);
          resultReadSucceeded = true;
          if (!options.onProviderResult) options.validateResult?.(result);
          return result;
        } catch (error) {
          if (signal?.aborted) throw createAbortError(signal);
          if (transportController.signal.aborted) throw createAbortError(transportController.signal);
          if (error?.completedResultRetentionFailed) throw error;
          if (resultReadSucceeded) {
            // The job finished and re-reading returns the same payload, so a rejected answer is final.
            const failure = new Error(`The finished job returned an unusable result: ${
              error?.message || 'validation failed'}`, { cause: error });
            failure.terminalProviderFailure = true;
            throw failure;
          }
          if (resumeRequestId && isMissingRequest(error)) {
            error.terminalProviderFailure = true;
            throw error;
          }
          if (!resultReadSucceeded && isDefiniteResultRejection(error)) {
            const detail = error?.body?.detail;
            const message = typeof detail === 'string' ? detail
              : Array.isArray(detail) ? detail.map(entry => entry?.msg).filter(Boolean).join('; ')
                : error?.message;
            const failure = new Error(message || error?.message || 'FAL job failed.', { cause: error });
            failure.terminalProviderFailure = true;
            throw failure;
          }
          lastReadError = error;
          readFailures += 1;
          if (readFailures >= maxReadFailures) throw error;
        }
      }
      await waitForQueuePoll(pollIntervalMs, transportController.signal);
    }
    const timeout = new Error('FAL job result remains unresolved after bounded polling.',
      { cause: lastReadError });
    timeout.name = 'TimeoutError';
    throw timeout;
  } catch (error) {
    if (callbackCancellation) throw callbackCancellation;
    throw markPaidQueueFailure(error, { requestId, submitted });
  } finally {
    if (deadlineTimer) globalThis.clearTimeout(deadlineTimer);
    if (!retainCancellationForLateReceipt) cancellation.release();
  }
}
